import React, { useState } from 'react';
import {
  Sun, Moon, Check, Zap, Shield, Clock, Users, Star,
  ArrowRight, MessageSquare, ChevronDown, Cpu, Globe, Award
} from 'lucide-react';

const LandingPage = ({ currentTheme, toggleTheme, onOpenAuth, landingContent = {}, API_BASE_URL }) => {
  const [openFaq, setOpenFaq] = useState(null);

  const features = [
    { icon: '⚡', color: 'text-yellow-500 bg-yellow-500/10', title: 'Instant AI Responses', desc: 'No more waiting for IT tickets. Get expert answers in seconds, 24/7, 365 days a year — including weekends and holidays.' },
    { icon: '🤖', color: 'text-blue-500 bg-blue-500/10', title: '13 Specialized AI Agents', desc: 'From Microsoft 365 to AWS, PowerShell to cybersecurity — each agent is deeply trained and prompted for its domain.' },
    { icon: '🔒', color: 'text-green-500 bg-green-500/10', title: 'Private & Secure', desc: 'All conversations are encrypted and private to your account. Your data is never used to train external models or sold.' },
    { icon: '👥', color: 'text-purple-500 bg-purple-500/10', title: 'Team Management', desc: 'Admin dashboard to manage users, assign roles, track usage, and customize AI agents for your entire organization.' },
    { icon: '📜', color: 'text-orange-500 bg-orange-500/10', title: 'Scripts & Automation', desc: 'Get production-ready PowerShell, Python, and Bash scripts with error handling and comments — ready to deploy.' },
    { icon: '💬', color: 'text-teal-500 bg-teal-500/10', title: 'Persistent Chat History', desc: 'Every conversation is saved and searchable. Reference past solutions, continue where you left off, anytime.' },
  ];

  const personas = [
    {
      tag: 'For Starters',
      emoji: '🌱',
      title: 'Just Getting Started with Tech?',
      desc: "Don't let IT confusion hold you back. Get plain-English answers to any tech question — from setting up email to securing your first device.",
      image: 'https://images.unsplash.com/photo-1484807352052-23338990c6c6?w=600&q=80',
      bullets: ['No IT background needed', 'Step-by-step guidance', 'Plain English explanations', 'Start free — no credit card'],
    },
    {
      tag: 'For Entrepreneurs',
      emoji: '🚀',
      title: 'Running a Business Solo?',
      desc: "Your time is your most valuable asset. Stop Googling for hours when something breaks — get the exact fix in minutes and get back to building.",
      image: 'https://images.unsplash.com/photo-1553484771-371a605b060b?w=600&q=80',
      bullets: ['M365 & Google Workspace', 'Cybersecurity & compliance', 'Automation & scripting', 'IT cost optimization tips'],
    },
    {
      tag: 'For Teams',
      emoji: '🏢',
      title: 'Scale IT Without Hiring',
      desc: "Give your whole team an AI IT assistant. Reduce help desk tickets, empower employees to self-serve, and keep business running smoothly.",
      image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&q=80',
      bullets: ['Multi-user admin dashboard', 'Role-based access control', 'Custom AI categories', 'Usage analytics & reports'],
    },
  ];

  const pricing = [
    {
      name: 'Starter',
      price: 'Free',
      period: '',
      desc: 'Try AI-powered IT support risk-free.',
      highlight: false,
      features: ['5 messages to explore', 'General IT support agent', '7-day chat history', 'Community support'],
      cta: 'Start Free',
      primary: false,
    },
    {
      name: 'Pro',
      price: '$20',
      period: '/month',
      desc: 'Unlimited access for individuals and solo professionals.',
      highlight: true,
      badge: '⭐ Most Popular',
      features: [
        'Unlimited messages',
        'All 13 AI specialists',
        'Full chat history',
        'Priority AI responses',
        'PowerShell & script generation',
        'Markdown + code output',
        'Email support',
      ],
      cta: 'Get Pro Access',
      primary: true,
    },
    {
      name: 'Business',
      price: '$99',
      period: '/month',
      desc: 'For teams that need centralized IT support at scale.',
      highlight: false,
      features: [
        'Everything in Pro',
        'Up to 10 team members',
        'Admin dashboard',
        'User & role management',
        'Custom AI categories',
        'Usage reports',
        'Priority email support',
      ],
      cta: 'Get Business',
      primary: false,
    },
  ];

  const testimonials = [
    {
      name: 'Sarah Chen',
      role: 'Founder, Brightwave Studio',
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&q=80',
      text: 'I was spending 3+ hours a week Googling IT fixes. Tech Assistant cut that to minutes. The M365 agent alone has saved me countless headaches.',
      stars: 5,
    },
    {
      name: 'Marcus Rodriguez',
      role: 'IT Manager, TechBridge Inc.',
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=80&q=80',
      text: 'We deployed this for our 8-person team and immediately saw a 60% drop in internal tickets. The PowerShell agent writes better scripts than our junior admin.',
      stars: 5,
    },
    {
      name: 'Priya Nair',
      role: 'Freelance Cloud Developer',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&q=80',
      text: 'The Azure and AWS agents are incredible. I got help configuring a VPC with proper security groups in 10 minutes — would have taken me 2 hours through docs.',
      stars: 5,
    },
  ];

  const agents = [
    { icon: '🏢', name: 'Microsoft 365' }, { icon: '🪟', name: 'Windows 11' },
    { icon: '🍎', name: 'macOS' }, { icon: '🔐', name: 'Active Directory' },
    { icon: '🛡️', name: 'Duo Security' }, { icon: '☁️', name: 'Azure Cloud' },
    { icon: '💻', name: 'PowerShell' }, { icon: '📜', name: 'Scripting' },
    { icon: '🚀', name: 'AWS' }, { icon: '📊', name: 'Splunk' },
    { icon: '✍️', name: 'Grammar & Writing' }, { icon: '📋', name: 'IT Reports' },
    { icon: '🎯', name: 'General IT' },
  ];

  const faqs = [
    { q: 'How is this different from just using ChatGPT?', a: 'Each of our 13 AI agents is specifically prompted and optimized for its IT domain. You get focused, expert answers without explaining context — just select your category and ask. Plus all conversations are saved, private, and organized.' },
    { q: 'Is my data secure?', a: 'Yes. All data is encrypted in transit and at rest. Your conversations are strictly private to your account and are never used to train external AI models or sold to third parties.' },
    { q: 'Can I cancel anytime?', a: 'Absolutely. No long-term contracts, no cancellation fees. Cancel anytime from your account and you retain access until the end of your billing period.' },
    { q: 'What happens after my 5 free messages?', a: "You'll be prompted to upgrade to Pro. Your chat history is preserved. Pro access is just $20/month for unlimited messages across all 13 AI specialists." },
    { q: 'Does it work for non-technical users?', a: "Yes — that's a core strength. The AI explains solutions in plain English with step-by-step instructions and screenshots descriptions. Zero IT background needed." },
    { q: 'Can I add my whole team?', a: 'Yes! The Business plan supports up to 10 users with a full admin dashboard for user management, role assignment, and usage analytics.' },
  ];

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className={`min-h-screen bg-background text-foreground theme-${currentTheme} antialiased`}>

      {/* ── Sticky Navbar ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/90 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <MessageSquare className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="text-base font-bold text-foreground hidden sm:block">Tech Assistant</span>
          </div>

          <div className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            {[['features', 'Features'], ['for-you', "Who It's For"], ['pricing', 'Pricing'], ['faq', 'FAQ']].map(([id, label]) => (
              <button key={id} onClick={() => scrollTo(id)} className="hover:text-foreground transition-colors">{label}</button>
            ))}
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <button onClick={toggleTheme} className="p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors" title="Toggle theme">
              {currentTheme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <button onClick={() => onOpenAuth('login')} className="text-sm text-foreground hover:text-primary font-medium transition-colors hidden sm:block">
              Sign In
            </button>
            <button onClick={() => onOpenAuth('register')} className="text-xs sm:text-sm px-3 sm:px-4 py-2 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-all shadow-md shadow-primary/20 flex items-center gap-1.5">
              Start Free <ArrowRight className="h-3 w-3" />
            </button>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="pt-28 pb-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/8 via-transparent to-purple-600/8 pointer-events-none" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/5 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center relative">
          {/* Left */}
          <div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-foreground leading-[1.1] mb-6">
              Your AI IT Team,<br />
              <span className="text-primary">Available 24/7</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-lg leading-relaxed">
              Stop waiting on IT tickets. Get instant expert help for Microsoft 365, Windows, Azure, cybersecurity, scripting, and more — all in one place, all the time.
            </p>
            <div className="flex flex-wrap gap-3 mb-10">
              <button
                onClick={() => onOpenAuth('register')}
                className="px-6 py-3.5 bg-primary text-primary-foreground rounded-xl font-bold hover:bg-primary/90 transition-all shadow-xl shadow-primary/30 flex items-center gap-2 text-sm"
              >
                Start Free — No Credit Card <ArrowRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => onOpenAuth('login')}
                className="px-6 py-3.5 border border-border bg-card text-foreground rounded-xl font-semibold hover:bg-muted transition-colors text-sm"
              >
                Sign In to Dashboard
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-green-500" /> 5 free messages</span>
              <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-green-500" /> No setup needed</span>
              <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-green-500" /> Cancel anytime</span>
            </div>
          </div>

          {/* Right — hero image + floating badges */}
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=900&q=80"
              alt="IT professionals collaborating"
              className="rounded-2xl shadow-2xl w-full object-cover"
              style={{ maxHeight: '440px' }}
            />
            <div className="absolute -bottom-4 -left-4 bg-card border border-border rounded-xl px-4 py-2.5 shadow-xl">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />
                <span className="text-xs font-semibold text-foreground">13 AI Specialists Online</span>
              </div>
            </div>
            <div className="absolute -top-3 -right-3 bg-card border border-border rounded-xl px-4 py-2.5 shadow-xl">
              <span className="text-xs font-semibold text-foreground">⚡ Avg Response: <span className="text-primary">&lt;2s</span></span>
            </div>
            <div className="absolute top-1/2 -left-6 bg-green-500 text-white rounded-xl px-3 py-1.5 shadow-xl hidden lg:block">
              <span className="text-xs font-bold">✓ Ticket Resolved</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats strip ── */}
      <section className="py-12 border-y border-border bg-muted/20">
        <div className="max-w-5xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { n: '13', label: 'AI Specialists' },
            { n: '24/7', label: 'Always Available' },
            { n: '<2s', label: 'Avg Response Time' },
            { n: '100%', label: 'Private & Secure' },
          ].map(s => (
            <div key={s.label}>
              <div className="text-3xl md:text-4xl font-extrabold text-primary mb-1">{s.n}</div>
              <div className="text-sm text-muted-foreground font-medium">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Agent scroll strip ── */}
      <section className="py-10 px-6 overflow-hidden">
        <p className="text-center text-sm text-muted-foreground mb-6 font-medium">13 specialized AI agents, one platform</p>
        <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
          {agents.map(a => (
            <div key={a.name} className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-card text-sm text-foreground hover:border-primary/50 hover:bg-primary/5 transition-colors cursor-default">
              <span>{a.icon}</span>
              <span className="font-medium text-xs">{a.name}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Features ── */}
      <section id="features" className="py-20 px-6 bg-muted/10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-4">Features</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-foreground mb-4">Everything You Need. Nothing You Don't.</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">Built specifically for IT support — not a general chatbot. Every feature is designed to get you from problem to solution faster.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map(f => (
              <div key={f.title} className="p-6 rounded-2xl border border-border bg-card hover:shadow-lg hover:border-primary/30 transition-all group">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl mb-4 ${f.color}`}>
                  {f.icon}
                </div>
                <h3 className="font-bold text-foreground mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Who it's for ── */}
      <section id="for-you" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-4">Who It's For</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-foreground mb-4">Built for Every Stage of Your Journey</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">Whether you're just starting out, running solo, or growing a team — we have exactly what you need.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {personas.map(p => (
              <div key={p.tag} className="rounded-2xl border border-border bg-card overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="relative">
                  <img src={p.image} alt={p.title} className="w-full h-48 object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-3 left-4">
                    <span className="text-2xl mr-2">{p.emoji}</span>
                    <span className="text-white text-sm font-bold bg-primary/80 px-2 py-0.5 rounded-full">{p.tag}</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-foreground mb-2">{p.title}</h3>
                  <p className="text-sm text-muted-foreground mb-5 leading-relaxed">{p.desc}</p>
                  <ul className="space-y-2">
                    {p.bullets.map(b => (
                      <li key={b} className="flex items-center gap-2 text-sm text-foreground">
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0" /> {b}
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => onOpenAuth('register')} className="mt-6 w-full py-2.5 border border-primary text-primary rounded-lg text-sm font-semibold hover:bg-primary hover:text-primary-foreground transition-colors">
                    Get Started Free →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="py-20 px-6 bg-muted/10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-4">How It Works</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-foreground mb-4">From Problem to Solution in 3 Steps</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">No onboarding. No tutorials. No IT jargon. Just ask and get your answer.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-8 left-1/3 right-1/3 h-px bg-border" />
            {[
              { step: '01', emoji: '🎯', title: 'Pick Your Specialist', desc: 'Choose from 13 AI agents trained for specific IT domains — M365, Windows, Azure, AWS, security, and more.' },
              { step: '02', emoji: '💬', title: 'Ask Your Question', desc: "Type your issue naturally. No ticket forms, no category trees. The AI understands context, intent, and follows up intelligently." },
              { step: '03', emoji: '✅', title: 'Get Expert Answers', desc: 'Receive detailed, step-by-step solutions with code snippets, CLI commands, and clear explanations — ready to use immediately.' },
            ].map(s => (
              <div key={s.step} className="text-center relative">
                <div className="w-16 h-16 rounded-2xl bg-card border-2 border-primary/30 flex flex-col items-center justify-center mx-auto mb-5 shadow-lg">
                  <span className="text-2xl">{s.emoji}</span>
                </div>
                <div className="text-xs font-bold text-primary mb-2 tracking-widest">STEP {s.step}</div>
                <h3 className="font-bold text-foreground mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="pricing" className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-4">Pricing</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-foreground mb-4">Simple, Transparent Pricing</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">Start free. Upgrade when you need more. No hidden fees, no annual lock-in, no surprise charges.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 items-start">
            {pricing.map(plan => (
              <div key={plan.name} className={`relative rounded-2xl border-2 bg-card p-8 flex flex-col ${plan.highlight ? 'border-primary shadow-2xl shadow-primary/20 scale-105' : 'border-border'}`}>
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap">
                    <span className="px-4 py-1.5 bg-primary text-primary-foreground text-xs font-bold rounded-full shadow-lg">{plan.badge}</span>
                  </div>
                )}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-foreground mb-2">{plan.name}</h3>
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-4xl font-extrabold text-foreground">{plan.price}</span>
                    <span className="text-muted-foreground">{plan.period}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{plan.desc}</p>
                </div>
                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-start gap-2 text-sm text-foreground">
                      <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => onOpenAuth('register')}
                  className={`w-full py-3 rounded-xl font-bold text-sm transition-all ${
                    plan.primary
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20'
                      : 'border border-border text-foreground hover:bg-muted'
                  }`}
                >
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-muted-foreground mt-10">
            💳 All plans start with 5 free messages. No credit card required.
          </p>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="py-20 px-6 bg-muted/10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-4">Testimonials</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-foreground mb-4">Loved by Professionals Like You</h2>
            <p className="text-muted-foreground">Real results from real users — no ticket system required.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map(t => (
              <div key={t.name} className="p-6 rounded-2xl border border-border bg-card hover:shadow-lg transition-shadow flex flex-col">
                <div className="flex gap-0.5 mb-4">
                  {[...Array(t.stars)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground mb-6 italic leading-relaxed flex-1">"{t.text}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-border">
                  <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" loading="lazy" />
                  <div>
                    <div className="font-semibold text-sm text-foreground">{t.name}</div>
                    <div className="text-xs text-muted-foreground">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Visual social proof banner ── */}
      <section className="py-16 px-6 bg-primary/5 border-y border-primary/10">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-muted-foreground text-sm mb-8 font-medium">Helping professionals with</p>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-4">
            {[
              { img: 'https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=200&q=80', label: 'M365 Support' },
              { img: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=200&q=80', label: 'Server & Cloud' },
              { img: 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=200&q=80', label: 'Cybersecurity' },
              { img: 'https://images.unsplash.com/photo-1587620962725-abab7fe55159?w=200&q=80', label: 'Scripting' },
              { img: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=200&q=80', label: 'Remote Work' },
              { img: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=200&q=80', label: 'Team IT' },
            ].map(item => (
              <div key={item.label} className="flex flex-col items-center gap-2">
                <img src={item.img} alt={item.label} className="w-14 h-14 rounded-xl object-cover shadow-md" loading="lazy" />
                <span className="text-xs text-muted-foreground font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-14">
            <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-4">FAQ</span>
            <h2 className="text-3xl font-extrabold text-foreground mb-4">Common Questions</h2>
            <p className="text-muted-foreground">Everything you need to know before getting started.</p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-border rounded-xl bg-card overflow-hidden">
                <button
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-muted/50 transition-colors gap-4"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="font-semibold text-foreground text-sm">{faq.q}</span>
                  <ChevronDown className={`h-4 w-4 text-muted-foreground flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 text-sm text-muted-foreground border-t border-border pt-4 leading-relaxed">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="py-20 px-6 bg-muted/10">
        <div className="max-w-3xl mx-auto">
          <div className="rounded-3xl bg-gradient-to-br from-primary/15 via-primary/5 to-purple-600/10 border border-primary/20 p-10 md:p-14 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent pointer-events-none" />
            <div className="relative">
              <div className="text-5xl mb-5">🚀</div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-foreground mb-4">
                Ready to Fix IT Problems Instantly?
              </h2>
              <p className="text-muted-foreground mb-8 max-w-lg mx-auto leading-relaxed">
                Join professionals and teams who've ditched slow IT tickets for instant AI-powered answers. Get started in 30 seconds — completely free.
              </p>
              <div className="flex flex-wrap justify-center gap-4 mb-6">
                <button
                  onClick={() => onOpenAuth('register')}
                  className="px-8 py-4 bg-primary text-primary-foreground rounded-xl font-bold hover:bg-primary/90 transition-all shadow-2xl shadow-primary/30 flex items-center gap-2"
                >
                  Create Free Account <ArrowRight className="h-5 w-5" />
                </button>
                <button
                  onClick={() => onOpenAuth('login')}
                  className="px-8 py-4 border border-border bg-card text-foreground rounded-xl font-bold hover:bg-muted transition-colors"
                >
                  Sign In
                </button>
              </div>
              <p className="text-xs text-muted-foreground">No credit card · No setup · Cancel anytime · 5 free messages to start</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-border py-12 px-6 bg-card">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 bg-primary rounded-lg flex items-center justify-center">
                  <MessageSquare className="h-3.5 w-3.5 text-primary-foreground" />
                </div>
                <span className="font-bold text-foreground">Tech Assistant</span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                AI-powered IT support for professionals, entrepreneurs, and teams. Available 24/7, no tickets required.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-3 text-sm">Product</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {[['features', 'Features'], ['for-you', "Who It's For"], ['pricing', 'Pricing'], ['faq', 'FAQ']].map(([id, label]) => (
                  <li key={id}><button onClick={() => scrollTo(id)} className="hover:text-foreground transition-colors">{label}</button></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-3 text-sm">Get Started</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><button onClick={() => onOpenAuth('register')} className="hover:text-foreground transition-colors">Create Free Account</button></li>
                <li><button onClick={() => onOpenAuth('login')} className="hover:text-foreground transition-colors">Sign In</button></li>
              </ul>
              <div className="mt-4">
                <button
                  onClick={() => onOpenAuth('register')}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors"
                >
                  Start Free →
                </button>
              </div>
            </div>
          </div>
          <div className="border-t border-border pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-muted-foreground">© 2025 Tech Assistant. All rights reserved.</p>
            <p className="text-xs text-muted-foreground">
              Developed by <span className="text-primary font-semibold">RG Integrated Solutions</span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
